// Phase D — Inline help callout for the web app.
//
// Collapsible info block with optional localStorage persistence (default open
// the first time the user lands on the page; collapsed on subsequent visits).
// Mirrors the mobile `InfoCallout` API but uses Tailwind classes + lucide-react.

import { ChevronDown, ChevronUp } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';

const STORAGE_PREFIX = 'leyrochain.info_callout.v1.';

export interface InfoCalloutProps {
  /** Persists open/closed state across reloads when provided. */
  storageKey?: string;
  /** Initial open state when no persisted value exists. Default: true. */
  defaultOpen?: boolean;
  /** Icon shown on the left side of the header (typically a lucide icon). */
  icon: ReactNode;
  /** Header title text. */
  title: string;
  /** Body content shown when expanded. */
  body: ReactNode;
}

function readPersistedOpen(storageKey: string | undefined, fallback: boolean): boolean {
  if (!storageKey) return fallback;
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(`${STORAGE_PREFIX}${storageKey}`);
    if (raw === null) return fallback;
    return raw === '1';
  } catch {
    return fallback;
  }
}

function writePersistedOpen(storageKey: string | undefined, open: boolean): void {
  if (!storageKey) return;
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(`${STORAGE_PREFIX}${storageKey}`, open ? '1' : '0');
  } catch {
    /* swallow quota / disabled-storage errors */
  }
}

export default function InfoCallout({
  storageKey,
  defaultOpen = true,
  icon,
  title,
  body,
}: InfoCalloutProps) {
  const [open, setOpen] = useState<boolean>(() => readPersistedOpen(storageKey, defaultOpen));

  useEffect(() => {
    writePersistedOpen(storageKey, open);
  }, [storageKey, open]);

  const toggle = () => setOpen((prev) => !prev);

  return (
    <div className="border border-border rounded-xl bg-subtle/40 p-4">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        className="flex items-center gap-3 cursor-pointer w-full text-left bg-transparent border-0 p-0 m-0"
      >
        <span className="shrink-0 flex items-center justify-center">{icon}</span>
        <span className="flex-1 font-semibold text-dark text-sm">{title}</span>
        <span className="shrink-0 text-muted">
          {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </span>
      </button>
      {open ? <div className="p-4 pt-3 text-sm text-muted leading-relaxed">{body}</div> : null}
    </div>
  );
}
